import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  UserQuery,
  UserGQL,
  User,
  Company,
  UserQueryVariables,
} from '@app/generated/graphql';
import * as Sentry from '@sentry/browser';
import { tap, catchError, map } from 'rxjs/operators';
import { ApolloQueryResult } from '@apollo/client/core';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private apollo = inject(Apollo);
  private userGql = inject(UserGQL);

  private _userSubject = new BehaviorSubject<User | null>(null);
  private _companySubject = new BehaviorSubject<Company | null>(null);

  user$: Observable<User> = this._userSubject.asObservable();
  company$: Observable<Company> = this._companySubject.asObservable();
  readonly companyName = toSignal(
    this._companySubject.pipe(map((c: Company | null) => c?.name ?? null)),
    { initialValue: this._companySubject.value?.name ?? null },
  );

  constructor() {}

  setUser(user: User): void {
    this._userSubject.next(user);
  }

  setCompany(company: Company): void {
    this._companySubject.next(company);
  }

  getUserValue(): User | null {
    return this._userSubject.value;
  }

  getCompanyValue(): Company | null {
    return this._companySubject.value;
  }

  fetchUserQueryData$(): Observable<UserQuery> {
    const variables: UserQueryVariables = {};

    return this.apollo
      .watchQuery<UserQuery>({
        query: this.userGql.document,
        variables: variables,
        fetchPolicy: 'no-cache',
      })
      .valueChanges.pipe(
        map((result: ApolloQueryResult<UserQuery>) => result.data),
        tap((userQuery: UserQuery) => {
          if (userQuery) {
            this.setUser(userQuery.myUser);
            this.setCompany(userQuery.myCompany);
          }
        }),
        catchError((error) => {
          Sentry.captureException(error);
          console.error('Error fetching user data:', error);
          return of(null); // Return a default/fallback value (or throw the error)
        }),
      );
  }
}
